import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { FiArrowLeft, FiVideo, FiCalendar, FiUsers, FiDownload, FiExternalLink } from 'react-icons/fi';

const ZoomPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-uc-navy hover:text-uc-gold transition-colors mb-4"
          >
            <FiArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-uc-navy font-header">ZOOM</h1>
          <p className="text-gray-600 mt-2">Virtual meetings and online classes</p>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-uc-navy mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="flex flex-col items-center gap-3 p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <FiVideo className="text-uc-gold" size={32} />
              <div className="text-center">
                <p className="font-semibold text-uc-navy">Join Meeting</p>
                <p className="text-sm text-gray-600">Enter meeting ID</p>
              </div>
            </button>
            <button className="flex flex-col items-center gap-3 p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <FiUsers className="text-uc-gold" size={32} />
              <div className="text-center">
                <p className="font-semibold text-uc-navy">New Meeting</p>
                <p className="text-sm text-gray-600">Start instant meeting</p>
              </div>
            </button>
            <button className="flex flex-col items-center gap-3 p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <FiCalendar className="text-uc-gold" size={32} />
              <div className="text-center">
                <p className="font-semibold text-uc-navy">Schedule</p>
                <p className="text-sm text-gray-600">Schedule new meeting</p>
              </div>
            </button>
          </div>
        </div>

        {/* Account Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-uc-navy mb-4">Your Zoom Account</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Account Type</p>
                <p className="font-semibold text-uc-navy">UC Merced Education</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Personal Meeting ID</p>
                <p className="font-mono font-semibold text-uc-navy">123-456-7890</p>
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Personal Meeting Link</p>
              <p className="font-mono text-uc-navy break-all">https://ucmerced.zoom.us/j/1234567890</p>
            </div>
          </div>
        </div>

        {/* Download Zoom */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-uc-navy mb-4">Download Zoom Client</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="flex flex-col items-center gap-3 p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <FiDownload className="text-uc-gold" size={32} />
              <div className="text-center">
                <p className="font-semibold text-uc-navy">Desktop App</p>
                <p className="text-sm text-gray-600">Windows & macOS</p>
              </div>
            </button>
            <button className="flex flex-col items-center gap-3 p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <FiDownload className="text-uc-gold" size={32} />
              <div className="text-center">
                <p className="font-semibold text-uc-navy">Mobile App</p>
                <p className="text-sm text-gray-600">iOS & Android</p>
              </div>
            </button>
            <button className="flex flex-col items-center gap-3 p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <FiDownload className="text-uc-gold" size={32} />
              <div className="text-center">
                <p className="font-semibold text-uc-navy">Browser Extension</p>
                <p className="text-sm text-gray-600">Chrome & Firefox</p>
              </div>
            </button>
          </div>
        </div>

        {/* Access Zoom Portal */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-4">
            <FiExternalLink className="text-blue-600 mt-1 flex-shrink-0" size={24} />
            <div className="flex-1">
              <h3 className="font-bold text-blue-900 mb-2">Access Zoom Web Portal</h3>
              <p className="text-blue-800 mb-4">
                Manage your Zoom settings, recordings, and scheduled meetings through the web portal.
              </p>
              <a
                href="https://ucmerced.zoom.us"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Open Zoom Portal <FiExternalLink size={18} />
              </a>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-bold text-uc-navy mb-4">About Zoom at UC Merced</h3>
          <div className="space-y-3 text-gray-700">
            <p>
              UC Merced provides Zoom for Education to all students, faculty, and staff. Features include:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Unlimited meeting duration</li>
              <li>Up to 300 participants per meeting</li>
              <li>Screen sharing and whiteboard</li>
              <li>Breakout rooms for group work</li>
              <li>Cloud recording and storage</li>
              <li>Calendar integration (Google Calendar, Outlook)</li>
              <li>Virtual backgrounds and filters</li>
              <li>Live transcription and closed captioning</li>
            </ul>
            <p className="text-sm text-gray-600 mt-4">
              For help with Zoom, visit the Zoom Help Center or contact IT Support at it@ucmerced.edu.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ZoomPage;
